import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  User,
  Package,
  Package2,
  Box,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  DollarSign,
  Truck,
  ShoppingBag,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Copy,
  ExternalLink,
  Edit,
  StickyNote,
  RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { formatDate, formatDateTime } from '../lib/utils';
import { Crown } from 'lucide-react';
import { Order } from '../types';
import { toast } from 'sonner';
import { getShippingTierName, autoSelectBoxForOrder } from '../services/shippingService';
import { EditOrderModal } from './EditOrderModal';
import { getShippingRates, getDefaultShippingConfig } from '../services/easypostService';

// Currency conversion helpers using order's stored metadata
const getOrderCurrency = (order: Order) => {
  return order?.purchaseCurrency || 'USD';
};

const getOrderExchangeRates = (order: Order) => {
  return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
};

// Convert USD price to order's display currency using stored exchange rate
const getConvertedItemPriceFromOrder = (usdPrice: number, order: Order) => {
  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);
  
  if (currency === 'USD') {
    return usdPrice;
  }
  
  const rate = rates[currency] || 1;
  return Math.ceil(usdPrice * rate);
};

// Format currency with order's currency symbol and code
const formatOrderCurrency = (amount: number, order: Order) => {
  const currency = getOrderCurrency(order);
  
  if (currency === 'USD') {
    return `$${amount.toFixed(2)} USD`;
  } else if (currency === 'CAD') {
    return `$${amount.toFixed(2)} CAD`;
  } else if (currency === 'GBP') {
    return `£${amount.toFixed(2)} GBP`;
  }
  
  return `$${amount.toFixed(2)}`;
};

const getCustomDutiesFromOrder = (order: Order) => {
  const duties = (order as any)?.customDuties;
  if (!duties || !duties.amountUsd) return 0;

  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);

  if (
    duties.currency &&
    duties.currency === currency &&
    typeof duties.amountConverted === 'number'
  ) {
    return duties.amountConverted;
  }

  const usdAmount = duties.amountUsd;
  if (currency === 'USD') return usdAmount;
  const rate = rates[currency as keyof typeof rates] || 1;
  return Math.ceil(usdAmount * rate);
};

// Helper function to get effective price (sale price if on sale, otherwise regular price)
const getEffectivePrice = (item: any) => {
  if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
    return item.onSalePrice;
  }
  return item.price;
};

// Calculate order total using per-item conversion for accuracy
const getOrderTotal = (order: Order) => {
  if (!order.items) return 0;
  
  // Calculate converted subtotal (per-item conversion)
  const convertedSubtotal = order.items.reduce((total, item) => {
    const unitPriceUsd = getEffectivePrice(item);
    const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, order);
    return total + (convertedUnitPrice * item.quantity);
  }, 0);
  
  // Calculate converted shipping (individual conversion)
  const baseShippingCost = order?.shipping?.cost || 0;
  const convertedShipping = order?.discount?.type === 'free_shipping' 
    ? 0 
    : getConvertedItemPriceFromOrder(baseShippingCost, order);
  
  // Calculate converted priority fee (individual conversion)
  const convertedPriorityFee = getConvertedItemPriceFromOrder(order?.shipping?.priorityFee || 0, order);
  
  // Calculate converted discount (individual conversion)
  const convertedDiscount = order?.discount?.type !== 'free_shipping' 
    ? getConvertedItemPriceFromOrder(order?.discount?.amount || 0, order) 
    : 0;

  const convertedDuties = getCustomDutiesFromOrder(order);
  
  // Total calculation: subtotal + shipping + priority - discount + duties
  return convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount + convertedDuties;
};

// Calculate order subtotal using per-item conversion
const getOrderSubtotal = (order: Order) => {
  if (!order.items) return 0;
  
  return order.items.reduce((total, item) => {
    const unitPriceUsd = getEffectivePrice(item);
    const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, order);
    return total + (convertedUnitPrice * item.quantity);
  }, 0);
};

interface OrderDetailsModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onPackOrder?: (order: Order) => void;
  onOrderUpdated?: (order: Order) => void;
}

interface DisplayShippingAddress {
  recipientName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
}

export function OrderDetailsModal({ order, isOpen, onClose, onPackOrder, onOrderUpdated }: OrderDetailsModalProps) {
  const navigate = useNavigate();
  const [shippingTierName, setShippingTierName] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [calculatingBox, setCalculatingBox] = useState(false);
  const [dhlRateLoading, setDhlRateLoading] = useState(false);
  const [dhlRateError, setDhlRateError] = useState<string | null>(null);
  const [dhlRate, setDhlRate] = useState<{ amount: string; currency: string; service?: string } | null>(null);
  
  // Helper function to check if order can be packed
  const canPackOrder = (order: Order) => {
    const isPaid = order.status === "paid";
    const isSucceeded = order.payment?.status === "succeeded";
    const isNotPacked = !order.packed;
    
    return isPaid && isSucceeded && isNotPacked;
  };
  
  // Initialize current order when order changes
  useEffect(() => {
    if (order) {
      setCurrentOrder(order);
    }
  }, [order]);

  // Load shipping tier name when order changes
  useEffect(() => {
    const loadShippingTierName = async () => {
      if (currentOrder?.shipping) {
        // Use 'option' field first (new format), fallback to 'method' (legacy)
        const shippingId = currentOrder.shipping.option || currentOrder.shipping.method || '';
        if (shippingId) {
          const tierName = await getShippingTierName(shippingId);
          setShippingTierName(tierName);
        } else {
          setShippingTierName('Unknown Shipping');
        }
      }
    };

    if (currentOrder && isOpen) {
      loadShippingTierName();
    }
  }, [currentOrder, isOpen]);
  
  if (!currentOrder) return null;

  const getOrderNumber = () => {
    return currentOrder.orderNumber || currentOrder.id;
  };

  const getPaymentStatus = () => {
    return currentOrder.payment?.status || 'pending';
  };

  const handleFetchDhlRate = async () => {
    if (!currentOrder?.id) return;
    setDhlRateLoading(true);
    setDhlRateError(null);
    try {
      const pkgId =
        currentOrder.shipping?.packageDimensions?.id ||
        currentOrder.shipping?.packageDimensions?.name || // legacy
        undefined;
      const shipFromId = (currentOrder as any)?.shipping?.shipFromAddressId || undefined;

      let resolvedShipFrom = shipFromId;
      let resolvedPkg = pkgId;

      if (!resolvedShipFrom || !resolvedPkg) {
        const defaults = await getDefaultShippingConfig();
        if (!resolvedShipFrom && defaults.shipFromAddress?.id) {
          resolvedShipFrom = defaults.shipFromAddress.id;
        }
        if (!resolvedPkg && defaults.packageDimensions?.id) {
          resolvedPkg = defaults.packageDimensions.id;
        }
      }

      if (!resolvedShipFrom || !resolvedPkg) {
        setDhlRateError('Missing ship-from or package dimensions to fetch rate.');
        return;
      }

      const rates = await getShippingRates(currentOrder.id, resolvedShipFrom, resolvedPkg);
      const dhl = rates.find((r: any) => (r.carrier || r.provider || '').toUpperCase().includes('DHL')) || rates[0];

      if (!dhl) {
        setDhlRateError('No DHL rate returned.');
        return;
      }

      setDhlRate({
        amount: dhl.rate || '',
        currency: dhl.currency || 'USD',
        service: dhl.service
      });
    } catch (error: any) {
      console.error('Failed to fetch DHL rate:', error);
      setDhlRateError(error?.message || 'Failed to fetch DHL rate.');
    } finally {
      setDhlRateLoading(false);
    }
  };

  const getTrackingNumber = () => {
    return currentOrder.tracking?.trackingNumber || null;
  };

  const getShippingAddressDetails = (): DisplayShippingAddress => {
    const shippingAddress = (currentOrder.shipping?.address || {}) as Record<string, any>;
    const customerAddress = (currentOrder.customer?.address || {}) as Record<string, any>;
    const legacyAddress = (currentOrder.customer?.addresses?.[0] || {}) as Record<string, any>;

    const resolvedAddress =
      (shippingAddress.line1 || shippingAddress.address1)
        ? shippingAddress
        : customerAddress.line1
          ? customerAddress
          : legacyAddress;

    return {
      recipientName: String(
        shippingAddress.fullName ||
        currentOrder.customer?.name ||
        legacyAddress.fullName ||
        ''
      ).trim(),
      line1: String(resolvedAddress.line1 || resolvedAddress.address1 || '').trim(),
      line2: String(resolvedAddress.line2 || resolvedAddress.address2 || '').trim(),
      city: String(resolvedAddress.city || '').trim(),
      state: String(resolvedAddress.state || '').trim(),
      postalCode: String(
        resolvedAddress.zipCode ||
        resolvedAddress.postal_code ||
        ''
      ).trim(),
      country: String(resolvedAddress.country || '').trim(),
      phone: String(
        shippingAddress.phoneNumber ||
        currentOrder.customer?.phone ||
        legacyAddress.phoneNumber ||
        ''
      ).trim(),
      email: String(currentOrder.customer?.email || '').trim(),
    };
  };

  const getEstimatedDelivery = () => {
    return currentOrder.tracking?.estimatedDelivery || null;
  };

  const orderNote = currentOrder.notes?.trim();
  const shippingAddressDetails = getShippingAddressDetails();
  const hasStructuredAddress = Boolean(
    shippingAddressDetails.line1 ||
    shippingAddressDetails.line2 ||
    shippingAddressDetails.city ||
    shippingAddressDetails.state ||
    shippingAddressDetails.postalCode ||
    shippingAddressDetails.country
  );

  const getProductName = (item: any) => {
    // Try different possible field names for product name
    return item.name || item.productName || item.title || 'Unknown Product';
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pending',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: Clock
        };
      case 'paid':
        return {
          label: 'Paid',
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: Package
        };
      case 'processed':
        return {
          label: 'Processed',
          color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
          icon: Package2
        };
      case 'shipped':
        return {
          label: 'Shipped',
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          icon: Truck
        };
      case 'delivered':
        return {
          label: 'Delivered',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: CheckCircle
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: XCircle
        };
      case 'payment_failed':
        return {
          label: 'Payment Failed',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: AlertCircle
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: Clock
        };
    }
  };

  const getPaymentStatusConfig = (status: string) => {
    switch (status) {
      case 'succeeded':
        return {
          label: 'Succeeded',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: CheckCircle
        };
      case 'pending':
        return {
          label: 'Pending',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: Clock
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: XCircle
        };
      case 'refund_pending':
        return {
          label: 'Refund Pending',
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          icon: AlertCircle
        };
      case 'refunded':
        return {
          label: 'Refunded',
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          icon: AlertCircle
        };
      // Legacy support for any old payment statuses that might exist
      case 'paid':
      case 'completed':
        return {
          label: 'Succeeded',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: CheckCircle
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: AlertCircle
        };
    }
  };

  const copyOrderNumber = () => {
    navigator.clipboard.writeText(getOrderNumber());
    toast.success('Order number copied to clipboard');
  };

  const copyOrderId = () => {
    navigator.clipboard.writeText(currentOrder.id);
    toast.success('Order ID copied to clipboard');
  };

  const statusConfig = getStatusConfig(currentOrder.status);
  const paymentConfig = getPaymentStatusConfig(getPaymentStatus());
  const StatusIcon = statusConfig.icon;
  const PaymentIcon = paymentConfig.icon;
  const isRushdOrder = !!(
    currentOrder.rushdMembership ||
    currentOrder.customer?.rushdMembership?.isMember
  );

  const handleGoToCustomer = () => {
    const customerIdentifier = currentOrder?.customer?.email || currentOrder?.customer?.name || currentOrder?.customer?.id;
    if (customerIdentifier) {
      navigate(`/customers?search=${encodeURIComponent(customerIdentifier)}`);
    }
  };

  const handleEditOrder = () => {
    setShowEditModal(true);
  };

  const handleOrderUpdated = (updatedOrder: Order) => {
    setCurrentOrder(updatedOrder);
    if (onOrderUpdated) {
      onOrderUpdated(updatedOrder);
    }
    setShowEditModal(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Order Details
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      <div className="flex items-center gap-2">
                        <span>ID: {currentOrder?.id}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={copyOrderId}
                          className="p-1 h-auto text-white hover:bg-white/20"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEditOrder}
                        className="flex items-center gap-2 text-white hover:bg-white/20 px-3 py-1 rounded-lg"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Edit Order</span>
                      </Button>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Order Note */}
                {orderNote && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border border-amber-200 rounded-2xl p-5 shadow-inner"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-white border border-amber-200 flex items-center justify-center shadow">
                          <StickyNote className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-amber-900">Order Note</h3>
                          <p className="text-sm text-amber-800">Visible to the customer in email updates</p>
                        </div>
                      </div>
                      <Badge className="bg-white/80 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-xs font-semibold tracking-wide">
                        Admin Added
                      </Badge>
                    </div>
                    <div className="bg-white/90 border border-amber-100 rounded-xl p-4">
                      <p className="text-sm sm:text-base text-amber-900 whitespace-pre-line leading-relaxed">
                        {orderNote}
                      </p>
                    </div>
                  </motion.div>
                )}
                {/* Order Overview */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                  {/* Order Status */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                          <Package2 className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Order Status</h3>
                      </div>
                      {isRushdOrder && (
                        <Badge className="bg-amber-50 text-amber-800 border border-amber-200 rounded-lg flex items-center gap-1">
                          <Crown className="w-3 h-3 text-amber-600" />
                          Rush&apos;d Order
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Badge className={`${statusConfig.color} border-transparent hover:${statusConfig.color} rounded-lg px-3 py-1.5`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Placed: {formatDateTime(currentOrder.createdAt)}</p>
                        {getEstimatedDelivery() && (
                          <p>Est. delivery: {formatDate(getEstimatedDelivery())}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Payment</h3>
                    </div>
                    <div className="space-y-3">
                      <Badge className={`${paymentConfig.color} border-transparent hover:${paymentConfig.color} rounded-lg px-3 py-1.5`}>
                        <PaymentIcon className="w-3 h-3 mr-1" />
                        {paymentConfig.label}
                      </Badge>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Method: {currentOrder.payment?.method || 'Card'}</p>
                        {currentOrder.payment?.transactionId && (
                          <p className="font-mono text-xs">
                            ID: {currentOrder.payment.transactionId.slice(-8)}
                          </p>
                        )}
                        {currentOrder.refund?.processedAt && (
                          <div className="pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-purple-700">
                              Refund Processed: {formatDateTime(currentOrder.refund.processedAt)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Total */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Total</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatOrderCurrency(getOrderTotal(currentOrder), currentOrder)}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Subtotal: {formatOrderCurrency(getOrderSubtotal(currentOrder), currentOrder)}</p>
                        {currentOrder.shipping?.cost !== undefined && currentOrder.shipping.cost > 0 && (
                          <p>Shipping: {formatOrderCurrency(getConvertedItemPriceFromOrder(currentOrder.shipping.cost, currentOrder), currentOrder)}</p>
                        )}
                        {currentOrder.shipping?.priorityProcessing && currentOrder.shipping?.priorityFee && currentOrder.shipping.priorityFee > 0 && (
                          <p>Priority Processing: {formatOrderCurrency(getConvertedItemPriceFromOrder(currentOrder.shipping.priorityFee, currentOrder), currentOrder)}</p>
                        )}
                        {getCustomDutiesFromOrder(currentOrder) > 0 && (
                          <p className="text-amber-700 font-medium">Customs Duties: {formatOrderCurrency(getCustomDutiesFromOrder(currentOrder), currentOrder)}</p>
                        )}
                        {currentOrder.discount && (currentOrder.discount.amount > 0 || currentOrder.discount.type === 'free_shipping') && (
                          <div className="p-2 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-semibold text-green-700">
                                  {currentOrder.discount.type === 'free_shipping' 
                                    ? '🚚 Free Shipping Applied' 
                                    : `🎉 Discount Applied (${currentOrder.discount.code})`}
                                </span>
                              </div>
                              <span className="text-sm font-semibold text-green-700">
                                -{formatOrderCurrency(getConvertedItemPriceFromOrder(currentOrder.discount.amount, currentOrder), currentOrder)}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="pt-3 space-y-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleFetchDhlRate}
                            disabled={dhlRateLoading}
                            className="w-full justify-center"
                          >
                            {dhlRateLoading ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Fetching DHL rate...</span>
                              </div>
                            ) : (
                              'Fetch DHL Rate'
                            )}
                          </Button>
                          {dhlRate && (
                            <div className="text-sm text-gray-800 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                              <span className="font-medium">DHL Rate{dhlRate.service ? ` (${dhlRate.service})` : ''}</span>
                              <span className="font-semibold text-jamaica-green">{dhlRate.amount} {dhlRate.currency}</span>
                            </div>
                          )}
                          {dhlRateError && (
                            <p className="text-xs text-red-600">{dhlRateError}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <Separator />

                {/* Customer Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {/* Customer Details */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Customer Information</h3>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleGoToCustomer}
                        className="flex items-center gap-2 hover:bg-purple-50 hover:text-purple-700 rounded-lg text-gray-600 transition-colors"
                        title="View customer details on Customers page"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-sm font-medium">View Customer</span>
                      </Button>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Name:</span>
                        <p className="text-sm text-gray-900 font-medium">{currentOrder.customer.name}</p>
                      </div>
                      {currentOrder.customer.phone && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Phone:</span>
                          <p className="text-sm text-gray-600">{currentOrder.customer.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Shipping Address</h3>
                    </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {shippingAddressDetails.recipientName && (
                          <div>
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Recipient</span>
                            <p className="mt-1 text-sm font-medium text-gray-900">
                              {shippingAddressDetails.recipientName}
                            </p>
                          </div>
                        )}
                        {shippingAddressDetails.phone && (
                          <div>
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Phone</span>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-700">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{shippingAddressDetails.phone}</span>
                            </div>
                          </div>
                        )}
                        {shippingAddressDetails.email && (
                          <div className="md:col-span-2">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</span>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-700 break-all">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span>{shippingAddressDetails.email}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Delivery Address</span>
                        {hasStructuredAddress ? (
                          <div className="mt-2 space-y-1 text-sm text-gray-700">
                            {shippingAddressDetails.line1 && <p>{shippingAddressDetails.line1}</p>}
                            {shippingAddressDetails.line2 && <p>{shippingAddressDetails.line2}</p>}
                            {(shippingAddressDetails.city || shippingAddressDetails.state || shippingAddressDetails.postalCode) && (
                              <p>
                                {shippingAddressDetails.city}
                                {shippingAddressDetails.city && shippingAddressDetails.state ? ', ' : ''}
                                {shippingAddressDetails.state}
                                {(shippingAddressDetails.city || shippingAddressDetails.state) && shippingAddressDetails.postalCode ? ' ' : ''}
                                {shippingAddressDetails.postalCode}
                              </p>
                            )}
                            {shippingAddressDetails.country && <p>{shippingAddressDetails.country}</p>}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-gray-500">No address provided</p>
                        )}
                      </div>
                    </div>
                    {/* Shipping Method */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-sm font-medium text-gray-700">Shipping Method:</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm bg-gray-50 text-gray-700 px-2 py-1 rounded-lg font-medium border border-gray-200">
                            <Truck className="w-3 h-3 inline mr-1" />
                            {shippingTierName || 'Loading...'}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({formatOrderCurrency(getConvertedItemPriceFromOrder(currentOrder.shipping.cost, currentOrder), currentOrder)})
                          </span>
                        </div>
                      </div>

                      {currentOrder.shipping?.priorityProcessing && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-sm font-medium text-gray-700">Priority Processing:</span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded-lg font-medium border border-blue-200">
                              ⚡ Enabled
                            </span>
                            {currentOrder.shipping.priorityFee && (
                              <span className="text-xs text-gray-500">
                                (+{formatOrderCurrency(getConvertedItemPriceFromOrder(currentOrder.shipping.priorityFee, currentOrder), currentOrder)})
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {getTrackingNumber() && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Tracking Number:</span>
                              {currentOrder.orderTracking?.service && (
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                  {currentOrder.orderTracking.service === 'easypost' ? 'EasyPost' : 'Ship24'} Auto-Tracking
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-mono text-blue-600 font-medium">
                              {getTrackingNumber()}
                            </p>
                            {currentOrder.tracking?.carrier && (
                              <p className="text-xs text-gray-500">
                                Carrier: {currentOrder.tracking.carrier}
                              </p>
                            )}
                            {currentOrder.orderTracking?.status && currentOrder.orderTracking.status !== 'pending' && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Status:</span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  currentOrder.orderTracking.status === 'delivered' 
                                    ? 'bg-green-100 text-green-800'
                                    : currentOrder.orderTracking.status === 'in_transit'
                                    ? 'bg-blue-100 text-blue-800'
                                    : currentOrder.orderTracking.status === 'out_for_delivery'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {currentOrder.orderTracking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </div>
                            )}
                            {currentOrder.orderTracking?.lastUpdated && (
                              <p className="text-xs text-gray-400">
                                Last updated: {formatDateTime(currentOrder.orderTracking.lastUpdated)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Package Dimensions */}
                      {currentOrder.shipping?.packageDimensions ? (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <Box className="w-4 h-4 text-emerald-600" />
                              Recommended Box
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (!currentOrder) return;
                                setCalculatingBox(true);
                                try {
                                  const pkg = await autoSelectBoxForOrder(currentOrder.id);
                                  setCurrentOrder({
                                    ...currentOrder,
                                    shipping: {
                                      ...currentOrder.shipping,
                                      packageDimensions: pkg || currentOrder.shipping.packageDimensions
                                    }
                                  });
                                  toast.success('Box dimensions recalculated and saved');
                                } catch (err: any) {
                                  console.error('Auto-select box failed', err);
                                  toast.error(err?.message || 'Failed to auto-select box');
                                } finally {
                                  setCalculatingBox(false);
                                }
                              }}
                              disabled={calculatingBox || !currentOrder}
                              className="flex items-center gap-2"
                            >
                              {calculatingBox ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Recalculating...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4" />
                                  Recalculate
                                </>
                              )}
                            </Button>
                          </div>
                          <div className="mt-2 text-sm text-gray-800">
                            <div className="font-semibold">{currentOrder.shipping.packageDimensions.name || 'Box'}</div>
                            <div className="text-xs text-gray-600">
                              {(currentOrder.shipping.packageDimensions.length ?? '?')} × {(currentOrder.shipping.packageDimensions.width ?? '?')} × {(currentOrder.shipping.packageDimensions.height ?? '?')} {currentOrder.shipping.packageDimensions.unit || 'in'}
                              {currentOrder.shipping.packageDimensions.weight ? ` • ${currentOrder.shipping.packageDimensions.weight} oz` : ''}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                              <Box className="w-4 h-4 text-gray-500" />
                              No box selected
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (!currentOrder) return;
                                setCalculatingBox(true);
                                try {
                                  const pkg = await autoSelectBoxForOrder(currentOrder.id);
                                  setCurrentOrder({
                                    ...currentOrder,
                                    shipping: {
                                      ...currentOrder.shipping,
                                      packageDimensions: pkg || currentOrder.shipping.packageDimensions
                                    }
                                  });
                                  toast.success('Box dimensions saved to order');
                                } catch (err: any) {
                                  console.error('Auto-select box failed', err);
                                  toast.error(err?.message || 'Failed to auto-select box');
                                } finally {
                                  setCalculatingBox(false);
                                }
                              }}
                              disabled={calculatingBox || !currentOrder}
                              className="flex items-center gap-2"
                            >
                              {calculatingBox ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Calculating...
                                </>
                              ) : (
                                <>
                                  <Box className="h-4 w-4" />
                                  Calculate Box
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                <Separator />

                {/* Order Items */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      Items Ordered ({currentOrder.items.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {currentOrder.items.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-300"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                              {item.image && item.image.trim() !== '' ? (
                                <img
                                  src={item.image}
                                  alt={getProductName(item)}
                                  className="w-full h-full object-cover transition-opacity duration-200"
                                  onError={(e) => {
                                    // Fallback to package icon if image fails to load
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                      const fallback = parent.querySelector('.fallback-icon') as HTMLElement;
                                      if (fallback) {
                                        fallback.style.display = 'flex';
                                      }
                                    }
                                  }}
                                />
                              ) : null}
                              {/* Fallback icon (always present but conditionally visible) */}
                              <div 
                                className={`fallback-icon w-full h-full items-center justify-center ${
                                  item.image && item.image.trim() !== '' ? 'hidden' : 'flex'
                                }`}
                              >
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {getProductName(item)}
                              </h4>
                              <div className="text-sm text-gray-600 mt-1">
                                <p>Quantity: {item.quantity}</p>
                                <p>Price: {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item), currentOrder), currentOrder)}</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item), currentOrder) * item.quantity, currentOrder)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer with Pack Order Button */}
            {onPackOrder && canPackOrder(currentOrder) && (
              <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Package className="h-4 w-4" />
                    <span>Order ready for packing</span>
                  </div>
                  <Button
                    onClick={() => onPackOrder(currentOrder)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Pack Order
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
      
      {/* Edit Order Modal */}
      <EditOrderModal
        order={currentOrder}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onOrderUpdated={handleOrderUpdated}
      />
    </AnimatePresence>
  );
} 
